import React, { useContext, useState, useMemo } from 'react';
import { Search, X, Plus, Minus, Pencil, Settings2, Trash2, AlertTriangle, Users } from 'lucide-react';
import type { WidgetDefinition, UserWidgetPreference } from '../types';
import { getTypeIcon, getTypeLabel } from '../utils/widgetTypeConfig';
import { WidgetBuilder } from './WidgetBuilder/WidgetBuilder';
import type { DashboardCategoryInput } from './WidgetBuilder/categories';
import { resolveWidgetPermissions } from '../ability';
import { DashboardContext } from '../useDashboardContext';
import { WidgetAccessDialog } from '../access/WidgetAccessDialog';
import { useDashboardTranslation } from '../i18n';

interface WidgetManagerPanelProps {
  isOpen: boolean;
  catalog: WidgetDefinition[];
  currentLayout: UserWidgetPreference[];
  /**
   * Administracion del catalogo. Gobierna el boton del Widget Builder (una capacidad global, que si
   * es un `isAdmin`) y sirve de **respaldo** por widget cuando el catalogo no trae `permissions`.
   */
  isAdmin?: boolean;
  /**
   * Capacidad de crear y editar definiciones de widget — lo que abre el Widget Builder.
   *
   * 🔴 Antes esa puerta la gateaba `isAdmin`, y eso dejaba **inerte** la capacidad fina del host:
   * quien podía escribir widgets sin ser administrador creaba uno por API y no veía el botón para
   * hacerlo. Son dos preguntas distintas y se habían colapsado en una — administrar el catálogo no
   * es lo mismo que poder añadirle algo.
   *
   * Sin valor cae a `isAdmin`, que es el comportamiento histórico: un host que no la pase no cambia.
   */
  canWriteWidgets?: boolean;
  /** Respaldo de visibilidad para hosts sin `permissions` en el DTO. Ver `resolveWidgetPermissions`. */
  userRoles?: string[];
  /**
   * Capacidad de cambiar la exposicion de un widget -a quien alcanza-. Gobierna el paso de permisos
   * del asistente. Sin valor cae a `isAdmin`.
   */
  canPublishWidgets?: boolean;
  userId?: string;
  /**
   * 🔴 **Si el Constructor ofrece conectar una fuente EXTERNA** — un Power BI, otro ERP, un API
   * público. Es un paso DENTRO del asistente, no la puerta de entrada.
   *
   * ⚠️ Hasta aquí hacía **dos trabajos**: abría la puerta del Constructor *y* ofrecía la fuente
   * externa. Por eso un analista con `canWriteWidgets` veía un botón llamado «Conectar API externa»
   * que el API le rechazaba — y por eso no se podía abrir el Constructor sin abrir también las
   * fuentes externas. Es el cuarto caso de *un dato que significa dos cosas a la vez*.
   */
  showExternalEndpoints?: boolean;
  /**
   * **Si la puerta del Constructor existe.** La otra mitad de la deuda anterior, ahora separada.
   *
   * Sin valor cae a `showExternalEndpoints`, así que **ningún host cambia de conducta** al actualizar:
   * quien pasaba el flag viejo sigue viendo exactamente lo mismo. Abrir el Constructor sin abrir las
   * fuentes externas pasa a ser **una decisión de producto de una línea**, que es lo que antes no se
   * podía tomar.
   */
  showWidgetBuilder?: boolean;
  onClose: () => void;
  onRequestAdd: (widget: WidgetDefinition) => void;
  onRequestModify: (widget: WidgetDefinition, preference: UserWidgetPreference) => void;
  onRemoveWidget: (instanceId: string) => void;
  onWidgetCreated: (definition: WidgetDefinition) => void | Promise<void>;
  onWidgetUpdated?: (definition: WidgetDefinition) => void;
  onDeleteWidget?: (widgetId: string) => void;
  /** Vocabulario de segmentación del host. Ver Step5Permissions. */
  /**
   * 🔴 **RETIRADO el 2026-09-14 (F3.b.5).** Era un paso a paso puro —página → panel → Constructor— y
   * el Constructor dejó de preguntarlo: la vertical la determina la categoría. Se quita en vez de
   * dejarlo ignorado, porque una prop que se acepta y no hace nada es la clase de cosa que alguien
   * rellena mañana creyendo que segmenta algo.
   *
   * Repartir a una vertical sigue existiendo: es el sujeto «un módulo» del **diálogo de acceso**, que
   * lo recibe por `accessAdapter.capabilities.modules` — otro camino y otro acto.
   */
  /**
   * 🔴 Desde F3 puede traer la **pareja** categoría→vertical, y por eso deja de ser `string[]`.
   * Este panel no la usa: solo la deja pasar al Constructor, que es quien deriva.
   */
  availableCategories?: DashboardCategoryInput[];
}


export const WidgetManagerPanel: React.FC<WidgetManagerPanelProps> = ({
  isOpen, catalog, currentLayout, isAdmin = false, canWriteWidgets, canPublishWidgets, userRoles = [], userId,
  showExternalEndpoints = false, showWidgetBuilder, onClose, onRequestAdd, onRequestModify,
  onRemoveWidget, onWidgetCreated, onWidgetUpdated, onDeleteWidget, availableCategories,
}) => {
  const { t } = useDashboardTranslation();

  // La puerta cae al flag viejo mientras el host no diga otra cosa: separar los dos conceptos no
  // debe mover a nadie de sitio en la versión en que se separan.
  const puertaDelConstructor = showWidgetBuilder ?? showExternalEndpoints;

  /**
   * 🔴 La misma entrada al diálogo de acceso que tiene la tarjeta, aquí también — y no es una
   * duplicación por comodidad: la tarjeta solo existe para los widgets que están EN tu tablero, y
   * los que hay que acotar suelen ser justo los que no te has puesto. Sin esta, para restringir un
   * widget del catálogo habría que agregárselo primero al tablero propio.
   *
   * Contexto crudo por lo mismo que en la tarjeta: un adaptador opcional no puede volver el provider
   * obligatorio para quien monte el panel por separado.
   */
  const accessAdapter = useContext(DashboardContext)?.accessAdapter;
  const [accessWidget, setAccessWidget] = useState<WidgetDefinition | null>(null);
  // La capacidad de escritura, con el respaldo histórico. Ver la prop.
  const puedeEscribir = canWriteWidgets ?? isAdmin;
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('Todas');
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [pendingDeleteWidget, setPendingDeleteWidget] = useState<WidgetDefinition | null>(null);

  const categories = useMemo(() => {
    const cats = new Set(catalog.map(w => w.categoria).filter(Boolean));
    return ['Todas', ...Array.from(cats)];
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    const inLayoutIds = new Set(currentLayout.map(p => p.widget_id));
    return catalog
      .filter(w => {
        const matchSearch = w.nombre.toLowerCase().includes(searchTerm.toLowerCase());
        const matchCategory = categoryFilter === 'Todas' || w.categoria === categoryFilter;
        return matchSearch && matchCategory;
      })
      .sort((a, b) => {
        const aIn = inLayoutIds.has(a.widget_id) ? 0 : 1;
        const bIn = inLayoutIds.has(b.widget_id) ? 0 : 1;
        return aIn - bIn;
      });
  }, [catalog, currentLayout, searchTerm, categoryFilter]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-y-0 right-0 w-[348px] bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">{t('dashboard.catalog.title')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Búsqueda y filtros */}
        <div className="p-4 border-b border-gray-100 space-y-3 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('dashboard.catalog.search')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  categoryFilter === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de widgets */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {filteredCatalog.length === 0 ? (
            <div className="text-center text-gray-500 mt-10 text-sm">{t('dashboard.catalog.empty')}</div>
          ) : (
            filteredCatalog.map(widget => {
              const pref = currentLayout.find(p => p.widget_id === widget.widget_id);
              const inLayout = !!pref;
              const TypeIcon = getTypeIcon(widget.tipo);
              const typeLabel = getTypeLabel(widget.tipo);
              /**
               * Quien puede borrar **este** widget lo dice el servidor, no un `isAdmin` global.
               * Antes era `!isSeed && isAdmin`: el mismo boton para todo widget con dueño, sin
               * mirar la ACL de la fila — aparecia para un admin frente a un widget que no le
               * corresponde, y el clic acababa en 403. `resolveWidgetPermissions` cae a esa misma
               * expresion cuando el host no manda `permissions`.
               */
              const { can_delete: canDelete } = resolveWidgetPermissions(widget, { userRoles, isAdmin });

              return (
                <div
                  key={widget.widget_id}
                  className={`p-3 rounded-lg border transition-all ${
                    inLayout ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <TypeIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <h4 className="text-sm font-semibold text-gray-800 truncate">{widget.nombre}</h4>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                        {widget.categoria}
                      </span>
                      {accessAdapter &&
                        (widget.permissions?.can_share === true || widget.permissions?.can_edit === true) && (
                        <button
                          onClick={() => setAccessWidget(widget)}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title={t('dashboard.access.dialogTitle')}
                          aria-label={`${t('dashboard.access.dialogTitle')} — ${widget.nombre}`}
                          data-testid={`catalog-access-${widget.widget_id}`}
                        >
                          <Users className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {canDelete && onDeleteWidget && (
                        <button
                          onClick={() => setPendingDeleteWidget(widget)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={t('dashboard.catalog.deleteWidget')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">{widget.descripcion}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{typeLabel}</span>
                    {inLayout ? (
                      <div className="flex gap-1">
                        <button
                          onClick={() => pref && onRemoveWidget(pref.id)}
                          className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
                        >
                          <Minus className="w-3.5 h-3.5" /> Quitar
                        </button>
                        <button
                          onClick={() => pref && onRequestModify(widget, pref)}
                          className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Modificar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => onRequestAdd(widget)}
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Agregar
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Botón Builder: quien puede ESCRIBIR widgets, no solo el administrador. `isAdmin` sigue
            gobernando lo que se puede hacer DENTRO del Builder —el paso de permisos y si el widget
            nace privado—, que son decisiones distintas de si la puerta existe.

            Y la puerta la gobierna `puertaDelConstructor`, no `showExternalEndpoints`: el nombre del
            botón dejaba ver que el flag hacía dos trabajos. Ahora el rótulo dice lo que hay detrás —
            «conectar un API externo» solo si de verdad se puede conectar uno. */}
        {puedeEscribir && puertaDelConstructor && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setIsBuilderOpen(true)}
              className="w-full py-2 bg-white border border-gray-300 text-gray-600 text-xs font-medium rounded shadow-sm hover:bg-gray-50 transition-all flex items-center justify-center gap-1.5"
            >
              <Settings2 className="w-3.5 h-3.5" />
              {showExternalEndpoints
                ? t('dashboard.builder.openExternal')
                : t('dashboard.builder.open')}
            </button>
          </div>
        )}
      </div>

      {/* El diálogo de acceso del widget elegido en la lista. */}
      {accessAdapter && accessWidget && (
        <WidgetAccessDialog
          widget={accessWidget}
          adapter={accessAdapter}
          isOpen
          onClose={() => setAccessWidget(null)}
        />
      )}

      <WidgetBuilder
        availableCategories={availableCategories}
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onSuccess={(w) => { onWidgetCreated(w); setIsBuilderOpen(false); }}
        /*
         * 🔴 F4.4 — la salida del aviso de reparto a medias: se cierra el Constructor y se abre el
         * diálogo de acceso SOBRE el indicador recién creado, que es donde se termina lo que quedó
         * sin terminar. Sin esto, el aviso diría «ábrelo en el diálogo» y no habría por dónde.
         */
        onNeedsAccessDialog={(w) => {
          onWidgetCreated(w);
          setIsBuilderOpen(false);
          setAccessWidget(w);
        }}
        isAdmin={isAdmin}
        canPublishWidgets={canPublishWidgets}
        showExternalEndpoints={showExternalEndpoints}
      />

      {/* Dialog de confirmación — Eliminar Widget del Catálogo */}
      {pendingDeleteWidget && onDeleteWidget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('dashboard.confirm.deleteTitle')}</h3>
                <p className="text-sm text-gray-500">{t('dashboard.confirm.deleteBody')}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              ¿Confirmas que quieres eliminar{' '}
              <span className="font-semibold">"{pendingDeleteWidget.nombre}"</span> del catálogo?
              Se eliminará para todos los usuarios que lo tengan agregado.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setPendingDeleteWidget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => { onDeleteWidget(pendingDeleteWidget.widget_id); setPendingDeleteWidget(null); }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Eliminar widget
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
